import { jsx as _jsx } from "react/jsx-runtime";
import { TableHeadRow, TableRowCells } from '@/shared/components';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { HideScrollerEdgeContainer } from '@/core/styled';
import { engRusLetterOrNumberOnlyRegex } from '@/core/utils/validation';
import { defaultIncreaseViewportValue } from '@/shared/constants';
import { catchArrowUpDownEventClassName } from '@/warehouse/constants';
import { getSelectedItem } from '@/warehouse/helpers';
import { closeSnackbar } from 'notistack';
import { useEventListener } from 'usehooks-ts';
import { useItemWorkflowTableStore } from './business';
import { createItemWorkflowTableComponents } from './ItemWorkflowTable.content';
// TODO: rename to GoodsWorkflowTable? GoodsProcessingTable (the best)? WorkflowTable (my latest the best)
// TODO: move all this components with ItemWorkflowTableTable.state from receipt/shared/components to somewhere
// like warehouse/components.
const ItemWorkflowVirtualizedTable = ({ data, isLoading, isFetching, isError, isWaitingForSocketConnection, error, columns, tableVirtuosoRef, disableCatchWindowFocus, increaseViewportBy, itemContentContext, itemContent, onRowDoubleClick, onEnterKeyDown, onDeleteKeyDown, onMinusKeyDown, onPlusKeyDown, onLetterOrNumberKeyDown, }) => {
    const currentItemIndex = useItemWorkflowTableStore((state) => state.currentItemIndex);
    const setCurrentItemIndex = useItemWorkflowTableStore((state) => state.setCurrentItemIndex);
    // // Freaking `focus()` is not working well enough. It doesn't make `document.activeElement` to be equal to focused element.
    // const [isTableFocused, setIsTableFocused] = useState<boolean>(false);
    const listRef = useRef(null);
    const rowCount = data?.length ?? 0;
    useEffect(() => {
        return () => {
            // Prevents `currentItemIndex` persist it's value even when there is already another table is shown.
            setCurrentItemIndex(-1);
            closeSnackbar();
        };
    }, [setCurrentItemIndex]);
    useEffect(() => {
        if (disableCatchWindowFocus)
            return;
        if (isLoading || rowCount === 0)
            return;
        listRef.current?.focus();
    }, [isLoading, rowCount, disableCatchWindowFocus]);
    // // TODO: to delete
    // console.log('document.activeElement', document.activeElement);
    const handleWindowArrowKeyDown = (event) => {
        if (disableCatchWindowFocus)
            return;
        // TODO: to delete
        // console.log('event.target', event.target);
        // console.log('event.target.classList', (event.target as HTMLElement)?.classList);
        // Catching focus inside table container on 'ArrowUp' and 'ArrowDown' events from body and other "watchable" elements.
        if (!((event.code === 'ArrowUp' || event.code === 'ArrowDown') &&
            (event.target?.tagName === 'BODY' ||
                // (event.target as HTMLElement)?.classList.contains(catchInsideWindowKeyDownEventClassName))
                event.target?.classList.contains(catchArrowUpDownEventClassName))))
            return;
        event.preventDefault();
        // Important! Prevents event from fire twice.
        event.stopImmediatePropagation();
        // Doesn't work
        listRef?.current?.focus();
        // Works
        const el = document.getElementById('item-workflow-table-container'); // TODO: now we have 2 tables with same id, unfortunately
        el?.focus();
        keyDownCallback(event);
    };
    useEventListener('keydown', handleWindowArrowKeyDown);
    const keyDownCallback = useCallback((event) => {
        // TODO: to delete
        // console.log('TABLE event.key', event.key);
        // console.log('TABLE event.code', event.code);
        // Or to use event.key === 'Enter'
        if ((event.code === 'Enter' || event.code === 'NumpadEnter') && onEnterKeyDown) {
            const currentItem = getSelectedItem(data, currentItemIndex);
            // Pass through even if currentItem is undefined to play error sound.
            onEnterKeyDown(currentItem);
            return;
        }
        if (event.code === 'Delete' && event.ctrlKey && onDeleteKeyDown) {
            // For Ctrl+Del
            const currentItem = getSelectedItem(data, currentItemIndex);
            if (currentItem)
                onDeleteKeyDown(currentItem);
            return;
        }
        if ((event.code === 'Equal' || event.code === 'NumpadAdd') && onPlusKeyDown) {
            // For +/=
            const currentItem = getSelectedItem(data, currentItemIndex);
            if (currentItem)
                onPlusKeyDown(currentItem);
            return;
        }
        if ((event.code === 'Minus' || event.code === 'NumpadSubtract') && onMinusKeyDown) {
            // For -/_
            const currentItem = getSelectedItem(data, currentItemIndex);
            if (currentItem)
                onMinusKeyDown(currentItem);
            return;
        }
        if (onLetterOrNumberKeyDown && (event.key === 'Backspace' || engRusLetterOrNumberOnlyRegex.test(event.key))) {
            onLetterOrNumberKeyDown(event);
            return;
        }
        let nextIndex = null;
        if (event.code === 'ArrowUp') {
            nextIndex = Math.max(0, currentItemIndex - 1);
        }
        else if (event.code === 'ArrowDown') {
            nextIndex = Math.min(rowCount - 1, currentItemIndex + 1);
        }
        if (nextIndex !== null) {
            // This `if` is a bugfix, when following below `tableVirtuosoRef.current?.scrollIntoView`'s `done()` callback is
            // not working, when even is redirected from <body> or any other "watched" component.
            // If it is fixed, this block should be removed, then the next 'if' below should be remove either.
            if (event.target !== listRef.current) {
                setCurrentItemIndex(nextIndex);
            }
            tableVirtuosoRef.current?.scrollIntoView({
                index: nextIndex,
                behavior: 'auto',
                done: () => {
                    setCurrentItemIndex(nextIndex);
                },
            });
            event.preventDefault();
        }
    }, [
        currentItemIndex,
        rowCount,
        data,
        onDeleteKeyDown,
        onEnterKeyDown,
        onLetterOrNumberKeyDown,
        onMinusKeyDown,
        onPlusKeyDown,
        setCurrentItemIndex,
        tableVirtuosoRef,
    ]);
    const scrollerRef = useCallback((element) => {
        if (element) {
            element.addEventListener('keydown', keyDownCallback);
            listRef.current = element;
        }
        else {
            listRef.current?.removeEventListener('keydown', keyDownCallback);
        }
    }, [keyDownCallback]);
    const fixedHeaderContent = useCallback(() => _jsx(TableHeadRow, { headColumns: columns }), [columns]);
    const rowContent = useCallback((index, item) => _jsx(TableRowCells, { columns: columns, item: item, rowNumber: index + 1 }), [columns]);
    const components = useMemo(() => createItemWorkflowTableComponents(), []);
    return (_jsx(HideScrollerEdgeContainer, { children: _jsx(TableVirtuoso, { ref: tableVirtuosoRef, 
            // TODO: to delete
            // ref={(el) => {
            // 	tableVirtuosoRef.current = el;
            // 	if (focusTableRef) focusTableRef.current = el;
            // }}
            scrollerRef: scrollerRef, data: data, context: {
                ...(itemContentContext || {}),
                currentItemIndex,
                columnCount: columns.length,
                isLoading,
                isFetching,
                isError,
                isWaitingForSocketConnection,
                error,
                isEmpty: !rowCount,
                setCurrentItemIndex,
                onRowDoubleClick,
                // No reason to pass, since we should not use them directly in outer components for the best practices.
                // onEnterKeyDown,
                // onDeleteKeyDown,
                // onMinusKeyDown,
                // onPlusKeyDown,
            }, components: components, 
            // components={{
            // 	Scroller: Scroller,
            // 	Table: VirtualizedTable,
            // 	TableHead: VirtualizedTableHead,
            // 	TableRow: VirtualizedTableRow,
            // 	TableBody: VirtualizedTableBody,
            // }}
            fixedHeaderContent: fixedHeaderContent, itemContent: itemContent ?? rowContent, 
            // TODO: try to refactor later. `?? 100` is only used to fix TableVirtuoso bug.
            // increaseViewportBy={increaseViewportBy ?? 100}
            increaseViewportBy: increaseViewportBy ?? defaultIncreaseViewportValue, 
            // isScrolling={setIsScrolling}
            // increaseViewportBy={increaseViewportBy ?? getIncreaseViewportValueByRowCount(rowCount)}
            tabIndex: 0 }) }));
};
export { ItemWorkflowVirtualizedTable };
